import React, { useState, useCallback } from "react";
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Input,
  Select,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Progress,
  Icon,
  List,
  ListItem,
  ListIcon,
  IconButton,
  Badge,
  useToast,
  Card,
  CardBody,
  CardHeader,
  Heading,
} from "@chakra-ui/react";
import { FiUpload, FiFile, FiX, FiCheck, FiAlertCircle } from "react-icons/fi";
import { useDropzone } from "react-dropzone";
import { usePreBoarding } from "../../../context/PreBoardingContext";

const DOCUMENT_TYPES = {
  identity: [
    { value: "aadhaar", label: "Aadhaar Card" },
    { value: "pan", label: "PAN Card" },
    { value: "passport", label: "Passport" },
    { value: "driving_license", label: "Driving License" },
    { value: "voter_id", label: "Voter ID" },
    { value: "address_proof", label: "Address Proof" },
    { value: "photo", label: "Photograph" },
  ],
  education: [
    { value: "marksheet", label: "Marksheet / Grade Sheet" },
    { value: "degree_certificate", label: "Degree Certificate" },
    { value: "provisional_certificate", label: "Provisional Certificate" },
  ],
  experience: [
    { value: "employment_letter", label: "Employment Letter" },
    { value: "experience_certificate", label: "Experience Certificate" },
    { value: "relieving_letter", label: "Relieving Letter" },
    { value: "salary_slips", label: "Salary Slips" },
  ],
  compliance: [
    {
      value: "background_verification_consent",
      label: "Background Verification Consent",
    },
    { value: "declaration_form", label: "Declaration Form" },
    { value: "nda", label: "Non-Disclosure Agreement (NDA)" },
    { value: "offer_letter_signed", label: "Signed Offer Letter" },
  ],
  policy: [
    { value: "hr_policy_acknowledgement", label: "HR Policy Acknowledgement" },
    { value: "it_usage_policy", label: "IT Usage Policy" },
    { value: "confidentiality_agreement", label: "Confidentiality Agreement" },
    { value: "code_of_conduct", label: "Code of Conduct" },
  ],
  payroll: [
    { value: "bank_details", label: "Bank Details / Cancelled Cheque" },
    { value: "nominee_form", label: "Nominee Form" },
    { value: "pf_form", label: "PF Form (Form 11)" },
    { value: "esi_form", label: "ESI Form" },
    { value: "pt_form", label: "Professional Tax Form" },
    { value: "lwf_form", label: "Labour Welfare Fund Form" },
  ],
  medical: [
    { value: "medical_fitness_form", label: "Medical Fitness Certificate" },
    { value: "medical_checkup_report", label: "Medical Checkup Report" },
    { value: "vaccination_certificate", label: "Vaccination Certificate" },
  ],
};

const ALLOWED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "text/plain": [".txt"],
  "application/zip": [".zip"],
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const DocumentUpload = ({ candidateId, onUploadComplete }) => {
  const { uploadDocument, uploadMultipleDocuments, loading } = usePreBoarding();
  const toast = useToast();

  const [uploadMode, setUploadMode] = useState("single"); // 'single' or 'multiple'
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [formData, setFormData] = useState({
    documentType: "",
    category: "",
    expectedJoiningDate: "",
    issueDate: "",
    expiryDate: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        toast({
          title: "Invalid files",
          description:
            "Some files were rejected. Please check file type and size.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }

      if (uploadMode === "single" && acceptedFiles.length > 0) {
        setSelectedFiles([acceptedFiles[0]]);
      } else {
        setSelectedFiles((prev) => [...prev, ...acceptedFiles].slice(0, 10));
      }
    },
    [uploadMode, toast]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ALLOWED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: uploadMode === "multiple",
    disabled: loading,
  });

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.documentType) {
      newErrors.documentType = "Document type is required";
    }
    if (!formData.expectedJoiningDate) {
      newErrors.expectedJoiningDate = "Expected joining date is required";
    }
    if (selectedFiles.length === 0) {
      newErrors.files = "Please select at least one file";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSingleUpload = async () => {
    if (!validateForm()) {
      return;
    }

    const data = new FormData();
    data.append("document", selectedFiles[0]);
    data.append("candidateId", candidateId);
    data.append("documentType", formData.documentType);
    data.append("expectedJoiningDate", formData.expectedJoiningDate);

    if (formData.issueDate) data.append("issueDate", formData.issueDate);
    if (formData.expiryDate) data.append("expiryDate", formData.expiryDate);
    if (formData.notes) data.append("notes", formData.notes);

    try {
      await uploadDocument(data);

      // Reset form
      setSelectedFiles([]);
      setFormData({
        documentType: "",
        category: "",
        expectedJoiningDate: "",
        issueDate: "",
        expiryDate: "",
        notes: "",
      });

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleMultipleUpload = async () => {
    if (selectedFiles.length === 0) {
      setErrors({ files: "Please select at least one file" });
      return;
    }

    if (!formData.expectedJoiningDate) {
      setErrors({ expectedJoiningDate: "Expected joining date is required" });
      return;
    }

    const data = new FormData();
    selectedFiles.forEach((file) => {
      data.append("documents", file);
    });
    data.append("candidateId", candidateId);
    data.append("expectedJoiningDate", formData.expectedJoiningDate);

    try {
      const result = await uploadMultipleDocuments(data);

      // Show detailed results
      if (result.uploaded && result.uploaded.length > 0) {
        toast({
          title: "Upload successful",
          description: `${result.uploaded.length} documents uploaded successfully`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      if (result.failed && result.failed.length > 0) {
        toast({
          title: "Some uploads failed",
          description: `${result.failed.length} documents failed to upload`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }

      // Reset form
      setSelectedFiles([]);
      setFormData({
        documentType: "",
        category: "",
        expectedJoiningDate: "",
        issueDate: "",
        expiryDate: "",
        notes: "",
      });

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleUpload = () => {
    if (uploadMode === "single") {
      handleSingleUpload();
    } else {
      handleMultipleUpload();
    }
  };

  const getAvailableDocumentTypes = () => {
    if (!formData.category) {
      return [];
    }
    return DOCUMENT_TYPES[formData.category] || [];
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <Card>
      <CardHeader>
        <HStack justify="space-between">
          <Heading size="md">Upload Documents</Heading>
          <HStack>
            <Button
              size="sm"
              variant={uploadMode === "single" ? "solid" : "outline"}
              colorScheme="blue"
              onClick={() => {
                setUploadMode("single");
                setSelectedFiles([]);
              }}
            >
              Single Upload
            </Button>
            <Button
              size="sm"
              variant={uploadMode === "multiple" ? "solid" : "outline"}
              colorScheme="blue"
              onClick={() => {
                setUploadMode("multiple");
                setSelectedFiles([]);
              }}
            >
              Bulk Upload
            </Button>
          </HStack>
        </HStack>
      </CardHeader>

      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* Upload Mode Instructions */}
          <Box bg="blue.50" p={3} borderRadius="md">
            <Text fontSize="sm" color="blue.700">
              {uploadMode === "single"
                ? "Upload a single document with detailed information"
                : "Upload multiple documents at once (max 10 files)"}
            </Text>
          </Box>

          {/* Form Fields */}
          <HStack spacing={4} align="start">
            <FormControl isInvalid={errors.category} flex={1}>
              <FormLabel>Document Category</FormLabel>
              <Select
                placeholder="Select category"
                value={formData.category}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    category: e.target.value,
                    documentType: "",
                  });
                  setErrors({ ...errors, category: "" });
                }}
              >
                <option value="identity">Identity Proof</option>
                <option value="education">Education Documents</option>
                <option value="experience">Experience Documents</option>
                <option value="compliance">Compliance Forms</option>
                <option value="policy">Policy Acknowledgements</option>
                <option value="payroll">Payroll Documents</option>
                <option value="medical">Medical Documents</option>
              </Select>
              <FormErrorMessage>{errors.category}</FormErrorMessage>
            </FormControl>

            {uploadMode === "single" && (
              <FormControl isInvalid={errors.documentType} flex={1}>
                <FormLabel>Document Type</FormLabel>
                <Select
                  placeholder="Select document type"
                  value={formData.documentType}
                  onChange={(e) => {
                    setFormData({ ...formData, documentType: e.target.value });
                    setErrors({ ...errors, documentType: "" });
                  }}
                  isDisabled={!formData.category}
                >
                  {getAvailableDocumentTypes().map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.documentType}</FormErrorMessage>
              </FormControl>
            )}
          </HStack>

          <HStack spacing={4}>
            <FormControl isInvalid={errors.expectedJoiningDate} flex={1}>
              <FormLabel>Expected Joining Date</FormLabel>
              <Input
                type="date"
                value={formData.expectedJoiningDate}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    expectedJoiningDate: e.target.value,
                  });
                  setErrors({ ...errors, expectedJoiningDate: "" });
                }}
              />
              <FormErrorMessage>{errors.expectedJoiningDate}</FormErrorMessage>
            </FormControl>

            {uploadMode === "single" && (
              <>
                <FormControl flex={1}>
                  <FormLabel>Issue Date (Optional)</FormLabel>
                  <Input
                    type="date"
                    value={formData.issueDate}
                    onChange={(e) =>
                      setFormData({ ...formData, issueDate: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl flex={1}>
                  <FormLabel>Expiry Date (Optional)</FormLabel>
                  <Input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) =>
                      setFormData({ ...formData, expiryDate: e.target.value })
                    }
                  />
                </FormControl>
              </>
            )}
          </HStack>

          {uploadMode === "single" && (
            <FormControl>
              <FormLabel>Notes (Optional)</FormLabel>
              <Input
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Add any additional notes"
              />
            </FormControl>
          )}

          {/* File Upload Dropzone */}
          <Box
            {...getRootProps()}
            border="2px dashed"
            borderColor={isDragActive ? "blue.500" : "gray.300"}
            borderRadius="md"
            p={8}
            textAlign="center"
            cursor="pointer"
            bg={isDragActive ? "blue.50" : "gray.50"}
            transition="all 0.2s"
            _hover={{ borderColor: "blue.400", bg: "blue.50" }}
          >
            <input {...getInputProps()} />
            <VStack spacing={2}>
              <Icon as={FiUpload} boxSize={12} color="blue.500" />
              <Text fontWeight="medium">
                {isDragActive
                  ? "Drop files here"
                  : "Drag & drop files here, or click to select"}
              </Text>
              <Text fontSize="sm" color="gray.600">
                Supported: PDF, DOC, DOCX, JPG, PNG (Max{" "}
                {uploadMode === "multiple" ? "10 files," : ""} 10MB each)
              </Text>
            </VStack>
          </Box>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <Box>
              <Text fontWeight="medium" mb={2}>
                Selected Files ({selectedFiles.length})
              </Text>
              <List spacing={2}>
                {selectedFiles.map((file, index) => (
                  <ListItem
                    key={index}
                    p={3}
                    bg="gray.50"
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <HStack flex={1}>
                      <ListIcon as={FiFile} color="blue.500" />
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontSize="sm" fontWeight="medium">
                          {file.name}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {formatFileSize(file.size)}
                        </Text>
                      </VStack>
                    </HStack>
                    <IconButton
                      icon={<FiX />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => removeFile(index)}
                      aria-label="Remove file"
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Error Display */}
          {errors.files && (
            <Box bg="red.50" p={3} borderRadius="md">
              <HStack>
                <Icon as={FiAlertCircle} color="red.500" />
                <Text fontSize="sm" color="red.700">
                  {errors.files}
                </Text>
              </HStack>
            </Box>
          )}

          {/* Upload Button */}
          <Button
            colorScheme="blue"
            size="lg"
            leftIcon={<FiUpload />}
            onClick={handleUpload}
            isLoading={loading}
            loadingText="Uploading..."
            isDisabled={selectedFiles.length === 0 || loading}
          >
            Upload{" "}
            {selectedFiles.length > 0 &&
              `(${selectedFiles.length} file${
                selectedFiles.length > 1 ? "s" : ""
              })`}
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default DocumentUpload;
